import React, { useEffect, useRef, useState, useCallback } from 'react';
import { cloneDeep } from 'lodash';
import classnames from 'classnames';
import { Container, Stack } from '@fiverr-private/layout_components';
import * as styles from '../common/styles.ve.css';
import {
  BriefSection,
  BriefSectionCtaType,
  DynamicSection,
  FileAttachmentData,
  SectionTypes,
  ViewSection,
} from '../../../../types';
import { useBriefContext } from '../../../../common/BriefContext';
import { SectionTitle } from '../../../common/SectionTitle';
import { EditWrapper } from '../EditWrapper';
import { useExcludeSection } from '../useExcludeSection';
import { useUpdateSection } from '../useUpdateSection';
import { isSectionEmpty } from '../../../../utils';
import { BRIEF_SECTION_CLASS_NAME } from '../../../../constants';
import { BriefSectionActions } from './BriefSectionActions';
import { BriefSectionEditArea, BriefSectionViewArea } from './SectionTypes';

export interface BriefSectionProps {
  isRequired?: boolean;
  section: BriefSection;
  onChange?: (content: object | string) => void;
  opacity?: number;
}

export const Section = ({ section, onChange, isRequired = false, opacity }: BriefSectionProps) => {
  const [content, setContent] = useState(section.content);
  const [attachments, setAttachments] = useState<FileAttachmentData[] | undefined>(section.attachments);
  const updateSectionCta = useRef<BriefSectionCtaType>(undefined);
  const {
    editedBrief,
    editedSectionId,
    setEditedSectionId,
    isBriefInEditMode,
    updateEditedBrief,
    lastIncludedSectionId,
    setLastIncludedSectionId,
    registerSectionRef,
  } = useBriefContext();

  const isEditing = editedSectionId === section.id;

  const hideSection = useExcludeSection();
  const updateSection = useUpdateSection();

  const shouldRemoveSection = lastIncludedSectionId === section.id;
  const [hasScrolledToEdit, setHasScrolledToEdit] = useState(false);

  // Create a ref callback to register the section ref for scrolling
  const sectionRefCallback = useCallback(
    (node: HTMLElement | null) => {
      registerSectionRef(section.id, node);
    },
    [section.id, registerSectionRef]
  );

  useEffect(() => {
    if (isEditing) {
      const savedSection = editedBrief?.sections?.find((_section) => _section.id === section.id);
      if (savedSection?.focused || section.focused) {
        updateSection(section, { focused: false });
      }
    }
  }, [isEditing, section, editedBrief, updateSection]);

  const onEditStartHandler = () => {
    setEditedSectionId(section.id);
    updateSection(section, { focused: true });
  };

  const onEditEndHandler = () => {
    setEditedSectionId(null);
    setLastIncludedSectionId('');
    setHasScrolledToEdit(false);
  };

  const onCancelHandler = () => {
    const savedSection = editedBrief?.sections?.find((_section) => _section.id === section.id);

    // cancelling just added section should return it to the excluded sections list
    if (shouldRemoveSection) {
      hideSection(section);
    }

    if (savedSection) {
      setContent(savedSection.content);
      setAttachments(savedSection.attachments);
    }

    onEditEndHandler();
  };

  const onEditEndHandlerWrapper = () => {
    onEditEndHandler();
    updateSectionCta.current = 'save';
  };

  const onCancelHandlerWrapper = () => {
    onCancelHandler();
    updateSectionCta.current = updateSectionCta.current = shouldRemoveSection ? 'remove' : 'cancel';
  };

  useEffect(() => {
    if (isEditing) {
      section.onEditStart?.(section);
    } else {
      const updatedContent: Partial<BriefSection> = {
        focused: false,
        content: cloneDeep(content),
        attachments: attachments?.length ? cloneDeep(attachments) : attachments,
      };

      onChange?.(content);

      updateSection(section, { ...updatedContent });
      section?.onEditEnd?.({ ...section, ...updatedContent }, updateSectionCta.current);
      updateSectionCta.current = undefined;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const forceUpdateContent = (newContent: any) => {
    updateSection(section, { content: cloneDeep(newContent) });
  };

  if (!section.included && !isRequired) {
    return null;
  }

  // Check showWhen condition
  if (section.showWhen && editedBrief && !section.showWhen(editedBrief)) {
    return null;
  }

  // Hide sections without ViewComponent when not editing
  if (section.type === SectionTypes.dynamic && !(section as DynamicSection).ViewComponent && !isEditing) {
    return null;
  }

  const TitleComponent = section.TitleComponent ? section.TitleComponent : SectionTitle;

  const onViewSectionEdit = () => {
    if (section.type === SectionTypes.view && !editedSectionId) {
      const viewSection = section as ViewSection;
      updateEditedBrief(null, viewSection.editSectionId, { focused: true });
    }
  };

  const onSectionClick = () => {
    if (isBriefInEditMode || isEditing || section.readOnly || section.disableViewInteraction) {
      return;
    }

    const startEditing = section.type === SectionTypes.view ? onViewSectionEdit : onEditStartHandler;

    startEditing();
  };

  const getCursor = () => {
    if (isEditing || isBriefInEditMode || section.readOnly || section.disableViewInteraction) {
      return 'default';
    }

    return 'pointer';
  };

  const isDisabledSaveAction = () => {
    if (!isEditing) return true;

    const isContentExists = section.type === SectionTypes.attachments ? !!attachments?.length : !!content;

    if (shouldRemoveSection && isContentExists) return false;

    if (section.type === SectionTypes.attachments) {
      const originalAttachments = section.attachments || [];
      const currentAttachments = attachments || [];

      if (originalAttachments.length !== currentAttachments.length) {
        return false;
      }

      const hasChanges = currentAttachments.some((current, index) => {
        const original = originalAttachments[index];
        return !original || current.id !== original.id || current.name !== original.name;
      });

      return !hasChanges;
    }

    if (!isContentExists) return true;

    return false;
  };

  const isEmptyAndNotEdited = isSectionEmpty(section) && !isEditing;

  const titleComponent = (
    <TitleComponent
      title={section.title!}
      isEditing={isEditing}
      section={section}
      color={isEmptyAndNotEdited ? 'grey_900' : 'black'}
    />
  );
  const isInlineSectionActions = !section.moveActionsToSide?.(section) && titleComponent !== null;

  const sectionActions = !section.disableViewInteraction && (
    <BriefSectionActions
      section={section}
      onEditStart={onEditStartHandler}
      isEditing={isEditing}
      onEditEnd={onEditEndHandler}
    />
  );

  const titleGapSize = isEmptyAndNotEdited ? '1' : '2.5';
  const titleGap = !isInlineSectionActions ? '0' : titleGapSize;

  const customMargin = section.customMargin?.(section, editedSectionId);

  return (
    <Stack
      style={{
        opacity,
      }}
      cursor={getCursor()}
      onClick={onSectionClick}
      direction={!isEditing && section.inline ? 'row' : 'column'}
      gap="1"
      paddingX="3"
      paddingY={section.disableViewInteraction ? undefined : '3'}
      {...customMargin}
      justifyContent={section.inline ? 'flexStart' : 'spaceBetween'}
      className={classnames(BRIEF_SECTION_CLASS_NAME, {
        [styles.sectionEmpty]: isEmptyAndNotEdited,
        [styles.sectionContainer]: !editedSectionId && !section.disableViewInteraction,
        [styles.sectionContainerNoViewInteraction]: !editedSectionId && section.disableViewInteraction,
        [styles.sectionContainerEditMode]: isEditing,
      })}
      ref={sectionRefCallback}
      borderRadius="xl"
      {...(isEditing
        ? {
            borderWidth: 'sm',
            borderStyle: 'solid',
          }
        : {})}
    >
      <Stack direction="row" justifyContent="spaceBetween" width="100%">
        <Stack direction="column" minWidth={0} flexGrow={1} gap={titleGap}>
          <Stack direction="row" width="100%">
            {section.title && titleComponent}
            {isInlineSectionActions && <Container marginLeft="auto">{sectionActions}</Container>}
          </Stack>
          <EditWrapper
            isEditing={isEditing}
            viewMode={
              <BriefSectionViewArea
                section={section}
                forceUpdateContent={section.id === 'budget_and_timeline' ? forceUpdateContent : undefined}
              />
            }
            editMode={
              <BriefSectionEditArea
                section={section}
                onAttachmentsChange={setAttachments}
                onChange={setContent}
                onEditEnd={onEditEndHandlerWrapper}
                onCancel={onCancelHandlerWrapper}
                shouldRemoveSection={shouldRemoveSection}
              />
            }
            isDisabled={isDisabledSaveAction()}
            onCancel={onCancelHandlerWrapper}
            onEditEnd={onEditEndHandlerWrapper}
            hideActions={section.hideActions}
            shouldRemoveSection={shouldRemoveSection}
            disableInitialScroll={section.disableInitialScroll && !hasScrolledToEdit}
            onScrolledToEdit={() => setHasScrolledToEdit(true)}
          />
        </Stack>
        {!isInlineSectionActions && <Stack alignItems="flexStart">{sectionActions}</Stack>}
      </Stack>
    </Stack>
  );
};
